(() => {
  const getNativeSize = root => root.querySelector('[data-aria-native-size]:checked')?.value || '';
  const getColor = root => root.querySelector('[data-aria-color-option]:checked')?.value || '';

  const getVariantAssets = root => {
    if (root.ariaVariantAssets) return root.ariaVariantAssets;
    const data = root.querySelector('[data-aria-variant-assets]');
    if (!data) return {};

    try {
      root.ariaVariantAssets = JSON.parse(data.textContent);
    } catch (error) {
      root.ariaVariantAssets = {};
    }

    return root.ariaVariantAssets;
  };

  const getGalleryAssets = root => {
    if (root.ariaGalleryAssets) return root.ariaGalleryAssets;
    const data = root.querySelector('[data-aria-gallery-assets]');
    if (!data) return {};

    try {
      root.ariaGalleryAssets = JSON.parse(data.textContent);
    } catch (error) {
      root.ariaGalleryAssets = {};
    }

    return root.ariaGalleryAssets;
  };

  const availableSlides = gallery => Array.from(gallery.querySelectorAll('[data-aria-gallery-slide]'));

  const renderColorGallery = (root, gallery, size, color) => {
    if (!gallery || !size || !color) return;
    gallery.querySelectorAll('[data-aria-generated]').forEach(item => item.remove());

    const width = size.includes('22') ? '22' : '18';
    const group = size.includes('Restraints') ? `restraints-${width}` : 'standard';
    const photos = getGalleryAssets(root)[group]?.[color] || [];
    const slides = gallery.querySelector('.aria-gallery__slides');
    const thumbs = gallery.querySelector('.aria-gallery__thumbs');

    photos.forEach((photo, index) => {
      const key = `photo-${index}`;
      const slide = document.createElement('li');
      slide.className = 'aria-gallery__slide';
      slide.hidden = true;
      slide.dataset.ariaGallerySlide = key;
      slide.dataset.ariaGenerated = 'true';

      const zoom = document.createElement('button');
      zoom.type = 'button';
      zoom.dataset.ariaZoom = '';
      zoom.dataset.ariaZoomUrl = photo.url;
      zoom.setAttribute('aria-label', `Enlarge ${photo.caption}`);

      const image = document.createElement('img');
      image.src = photo.url;
      image.alt = photo.alt;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 1600;
      image.height = 1600;
      zoom.appendChild(image);

      slide.appendChild(zoom);
      slides.appendChild(slide);

      if (thumbs) {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.setAttribute('role', 'tab');
        thumb.setAttribute('aria-selected', 'false');
        thumb.setAttribute('aria-label', `View ${photo.caption}`);
        thumb.dataset.ariaGalleryThumb = key;
        thumb.dataset.ariaGenerated = 'true';

        const thumbImage = document.createElement('img');
        thumbImage.src = photo.url;
        thumbImage.alt = '';
        thumbImage.loading = 'lazy';
        thumb.appendChild(thumbImage);
        thumbs.appendChild(thumb);
      }
    });

    selectGallerySlide(gallery, 'variant');
  };

  const selectGallerySlide = (gallery, key) => {
    if (!gallery) return;
    const slides = availableSlides(gallery);
    const requested = slides.find(slide => slide.dataset.ariaGallerySlide === key) || slides[0];
    if (!requested) return;

    gallery.querySelectorAll('[data-aria-gallery-slide]').forEach(slide => {
      const active = slide === requested;
      slide.classList.toggle('is-active', active);
      slide.hidden = !active;
    });

    gallery.querySelectorAll('[data-aria-gallery-thumb]').forEach(thumb => {
      const active = thumb.dataset.ariaGalleryThumb === requested.dataset.ariaGallerySlide;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  };

  const updateGalleryNavigation = (gallery, direction) => {
    const slides = availableSlides(gallery);
    const current = slides.findIndex(slide => slide.classList.contains('is-active'));
    const next = direction === 'next'
      ? (current + 1) % slides.length
      : (current - 1 + slides.length) % slides.length;
    selectGallerySlide(gallery, slides[next]?.dataset.ariaGallerySlide);
  };

  const syncVisibleControls = root => {
    const size = getNativeSize(root);
    const width = size.includes('22') ? '22' : '18';
    const setup = size.includes('Restraints') ? 'restraints' : 'standard';
    const widthControl = Array.from(root.querySelectorAll('[data-aria-width]')).find(input => input.value === width);
    const setupControl = Array.from(root.querySelectorAll('[data-aria-setup]')).find(input => input.value === setup);
    if (widthControl) widthControl.checked = true;
    if (setupControl) setupControl.checked = true;
  };

  const updateAriaSelection = root => {
    if (!root) return;

    const size = getNativeSize(root);
    const color = getColor(root);
    const hasRestraints = size.includes('Restraints');
    const sizeLabel = size.replace(' Wide', ' wide').replace(' (Restraints)', '');
    const sizeBadge = root.querySelector('[data-aria-variant-size]');
    const colorBadge = root.querySelector('[data-aria-variant-colour]');
    const restraintBadge = root.querySelector('[data-aria-variant-restraints]');
    const colorNote = root.querySelector('[data-aria-color-note]');
    const gallery = root.querySelector('[data-aria-gallery]');

    root.classList.toggle('is-restraint-ready', hasRestraints);
    if (sizeBadge) sizeBadge.textContent = sizeLabel;
    if (colorBadge) colorBadge.textContent = color;
    if (restraintBadge) restraintBadge.hidden = !hasRestraints;
    if (colorNote) colorNote.hidden = !hasRestraints;

    if (!size || !color) return;
    const imageUrl = getVariantAssets(root)[size]?.[color];
    if (!imageUrl) return;

    const image = root.querySelector('[data-aria-selected-image]');
    const thumb = root.querySelector('[data-aria-selected-thumb]');
    const zoomButton = image?.closest('[data-aria-zoom]');
    const selectedThumb = root.querySelector('[data-aria-gallery-thumb="variant"]');
    const alt = `The Flip ${size} in ${color}`;
    if (image) {
      image.src = imageUrl;
      image.removeAttribute('srcset');
      image.alt = alt;
    }
    if (thumb) {
      thumb.src = imageUrl;
      thumb.removeAttribute('srcset');
    }
    if (zoomButton) zoomButton.dataset.ariaZoomUrl = imageUrl;
    if (selectedThumb) selectedThumb.setAttribute('aria-label', `View ${color} ${size}`);
    renderColorGallery(root, gallery, size, color);
    syncVisibleControls(root);
  };

  const selectNativeSize = (root, returnFocus) => {
    const width = root.querySelector('[data-aria-width]:checked')?.value || '18';
    const setup = root.querySelector('[data-aria-setup]:checked')?.value || 'standard';
    const exactSize = `${width}\" Wide${setup === 'restraints' ? ' (Restraints)' : ''}`;
    const target = Array.from(root.querySelectorAll('[data-aria-native-size]')).find(input => input.value === exactSize);
    if (!target) return;

    // Keep the selected color only when it exists for the target size/setup.
    // Bone Ivory is exclusive to the 18-inch restraint-ready option.
    if (!getVariantAssets(root)[exactSize]?.[getColor(root)]) {
      const black = Array.from(root.querySelectorAll('[data-aria-color-option]')).find(input => input.value === 'Black');
      if (black) black.checked = true;
    }

    root.querySelectorAll('[data-aria-native-size]').forEach(input => { input.checked = input === target; });
    root.ariaReturnFocus = returnFocus;
    updateAriaSelection(root);
    target.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const initializeRoot = root => {
    if (root.dataset.ariaInitialized === 'true') return;
    root.dataset.ariaInitialized = 'true';
    updateAriaSelection(root);

    let scheduled = false;
    const observer = new MutationObserver(mutations => {
      if (mutations.every(mutation => mutation.target.closest?.('[data-aria-gallery]'))) return;
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        root.ariaVariantAssets = null;
        updateAriaSelection(root);
        if (root.ariaReturnFocus?.isConnected) root.ariaReturnFocus.focus();
        root.ariaReturnFocus = null;
      });
    });
    observer.observe(root, { childList: true, subtree: true });
  };

  document.addEventListener('change', event => {
    const root = event.target.closest('.product-info--aria');
    if (!root) return;

    if (event.target.matches('[data-aria-width], [data-aria-setup]')) {
      selectNativeSize(root, event.target);
      return;
    }

    if (event.target.matches('[data-aria-color-option], [data-aria-native-size]')) {
      updateAriaSelection(root);
    }
  });

  document.addEventListener('click', event => {
    const root = event.target.closest('.product-info--aria');
    if (!root) return;
    const gallery = root.querySelector('[data-aria-gallery]');
    const thumb = event.target.closest('[data-aria-gallery-thumb]');
    if (thumb) {
      selectGallerySlide(gallery, thumb.dataset.ariaGalleryThumb);
      return;
    }
    if (event.target.closest('[data-aria-gallery-next]')) {
      updateGalleryNavigation(gallery, 'next');
      return;
    }
    if (event.target.closest('[data-aria-gallery-prev]')) {
      updateGalleryNavigation(gallery, 'prev');
      return;
    }

    const zoom = event.target.closest('[data-aria-zoom]');
    if (zoom) {
      const dialog = gallery?.querySelector('[data-aria-gallery-dialog]');
      const dialogImage = dialog?.querySelector('[data-aria-gallery-dialog-image]');
      const sourceImage = zoom.querySelector('img');
      if (dialog && dialogImage) {
        dialogImage.src = zoom.dataset.ariaZoomUrl || sourceImage?.currentSrc || sourceImage?.src;
        dialogImage.alt = sourceImage?.alt || 'Enlarged Flip product image';
        dialog.showModal();
      }
      return;
    }

    if (event.target.closest('[data-aria-gallery-close]')) {
      gallery?.querySelector('[data-aria-gallery-dialog]')?.close();
    }
  });

  const initialize = () => document.querySelectorAll('.product-info--aria').forEach(initializeRoot);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
